import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { fetchCollectionByHandle, shopifyImg, shopifySrcSet } from "@/lib/shopify";
import { useLocale } from "@/contexts/LocaleContext";
import { ProductCard } from "@/components/ProductCard";
import { ShopMobileEditorial } from "@/components/shop/ShopMobileEditorial";
import { SiteShell } from "@/components/SiteShell";
import { CollectionTabsNav } from "@/components/CollectionTabsNav";
import { SiteBreadcrumbs } from "@/components/SiteBreadcrumbs";
import { ShopAllFragrancesBar } from "@/components/ShopAllFragrancesBar";
import { useHeroVideoUrls } from "@/hooks/useHeroVideoUrls";
import { useGa4ViewItemList } from "@/hooks/useGa4ViewItemList";

import { HeroMedia } from "@/components/HeroMedia";
import { useCollectionHeroText } from "@/hooks/usePageHeroText";
import forHimHero from "@/assets/collection-for-him-hero.webp";
import oudScentMobileHero from "@/assets/oud-scent-hero-mobile.webp.asset.json";

import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";

// Map specific collection handles → hero campaign page keys
// (allows `/admin/hero-media` campaigns to override the Shopify hero on these pages).
const HERO_CAMPAIGN_PAGE_KEY: Record<string, string> = {
  "gift-set-perfume-collection": "collection-large",
  "ladybag-collection": "collection-ladybag",
};

// Local hero overrides per collection handle (replaces Shopify video/image).
const HERO_IMAGE_OVERRIDES: Record<string, string> = {
  "for-him": forHimHero,
  "perfume-for-man": forHimHero,
};

// Override صورة الهيرو على الموبايل فقط — الديسكتوب يبقي صورة/فيديو Shopify.
const HERO_MOBILE_IMAGE_OVERRIDES: Record<string, string> = {
  "oud-scent": oudScentMobileHero.url,
};

// Override لعنوان الهيرو على الموقع — يطغى على عنوان Shopify لجعل
// اسم القائمة المنسدلة (For Him/For Her/Unisex) متطابقاً مع عنوان الصفحة.
// القيمة هي مفتاح i18n (يُترجَم آلياً للعربيّ/الإنجليزيّ).
const HERO_TITLE_I18N_OVERRIDE: Record<string, string> = {
  "for-him": "nav.for_him",
  "perfume-for-man": "nav.for_him",
  "perfume-for-woman": "nav.for_her",
  "unisex-perfume": "nav.unisex",
};


import { publicCacheHeaders } from "@/lib/public-cache-headers";

export const Route = createFileRoute("/collection/$handle")({
  headers: publicCacheHeaders,
  loader: async ({ params }) => {
    try {
      const result = await fetchCollectionByHandle(params.handle, "AE", 50);
      return { ssrData: result };
    } catch {
      return { ssrData: null };
    }
  },
  head: ({ params, loaderData }) => {
    const col = loaderData?.ssrData?.collection;
    const title = col ? `${col.title} — Lecmo` : `${params.handle} — Lecmo`;
    const description = col?.description
      ? col.description.slice(0, 155)
      : `Browse the ${params.handle} collection by Lecmo.`;
    const meta: Array<Record<string, string>> = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
    ];
    if (col?.image?.url) {
      meta.push(
        { property: "og:image", content: shopifyImg(col.image.url, 1200, 630) },
        { name: "twitter:image", content: shopifyImg(col.image.url, 1200, 630) },
      );
    }
    // JSON-LD CollectionPage schema
    const jsonLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: col?.title ?? params.handle,
      description: description,
    };
    if (col?.image?.url) jsonLd.image = shopifyImg(col.image.url, 1200, 630);

    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Lecmo", item: `${PUBLIC_SITE_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: "Shop", item: `${PUBLIC_SITE_ORIGIN}/shop` },
        { "@type": "ListItem", position: 3, name: col?.title ?? params.handle, item: `${PUBLIC_SITE_ORIGIN}/collection/${params.handle}` },
      ],
    };

    return {
      meta,
      links: [
        { rel: "canonical", href: `${PUBLIC_SITE_ORIGIN}/collection/${params.handle}` },
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
      ],
    };
  },
  component: CollectionPage,
  notFoundComponent: () => (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-32 text-center sm:px-6">
        <h1 className="t-h1">Collection not found</h1>
        <Link to="/" className="mt-6 inline-block t-eyebrow-lg text-primary">
          ← Back home
        </Link>
      </div>
    </SiteShell>
  ),
  errorComponent: ({ error }) => (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-32 text-center sm:px-6">
        <h1 className="t-h1">Something went wrong</h1>
        <p className="mt-4 t-body text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      </div>
    </SiteShell>
  ),
});

function pickLocalized(
  ar: string | null | undefined,
  en: string | null | undefined,
  locale: string,
  fallback: string,
): string {
  const a = (ar ?? "").trim();
  const e = (en ?? "").trim();
  if (locale.startsWith("ar")) return a || e || fallback;
  return e || a || fallback;
}

function CollectionPage() {
  const { handle } = Route.useParams();
  const { t, locale, effectiveCountryCode } = useLocale();
  const countryCode = effectiveCountryCode;
  const { ssrData } = Route.useLoaderData();
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => setHydrated(true), []);
  const market = hydrated ? countryCode : "AE";


  const { data, isLoading, isError } = useQuery({
    queryKey: ["collection", handle, market],
    queryFn: () => fetchCollectionByHandle(handle, market, 50),
    enabled: hydrated,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    // بيانات SSR بسعر الإمارات فقط — لا تُبذَر في أسواق أخرى.
    initialData: market === "AE" ? (ssrData ?? undefined) : undefined,
  });

  useGa4ViewItemList({
    listId: handle,
    listName: data?.collection?.title ?? handle,
    products: data?.products ?? [],
  });

  const hasHeroOverride = !!HERO_IMAGE_OVERRIDES[handle];
  // إن لم تُوجد المجموعة في Shopify لكن لدينا صورة هيرو محليّة لهذا الـhandle
  // → نعرض الهيرو + شبكة فارغة بدل 404 الكاملة.
  if (!isLoading && data === null && !hasHeroOverride) throw notFound();
  if (isError && !hasHeroOverride) throw new Error("Failed to load collection");

  const collection = data?.collection;
  const products = data?.products ?? [];



  // Hero campaign override (managed in /admin/hero-media) — only for mapped handles.
  const heroCampaignKey = HERO_CAMPAIGN_PAGE_KEY[handle] ?? "";
  const heroCampaign = useHeroVideoUrls(heroCampaignKey || "home");
  const hasCampaignMedia = !!heroCampaignKey && !!(heroCampaign.desktop || heroCampaign.mobile || heroCampaign.poster);

  // نصوص الهيرو من /admin/page-backgrounds?tab=texts (تتقدّم على عنوان Shopify)
  const adminHeroText = useCollectionHeroText(handle);

  return (
    <SiteShell>
      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-onyx h-[calc(100svh-96px)] min-h-[620px] md:h-[60vh] md:min-h-[480px] md:max-h-[720px]">
        {HERO_MOBILE_IMAGE_OVERRIDES[handle] ? (
          <img
            src={HERO_MOBILE_IMAGE_OVERRIDES[handle]}
            alt={collection?.title ?? handle}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover md:hidden"
          />
        ) : null}
        <div
          className={
            HERO_MOBILE_IMAGE_OVERRIDES[handle]
              ? "absolute inset-0 hidden md:block"
              : "contents"
          }
        >
          {hasCampaignMedia ? (
            <HeroMedia
              videoUrl={heroCampaign.desktop}
              mobileVideoUrl={heroCampaign.mobile}
              poster={heroCampaign.poster || (collection?.image ? shopifyImg(collection.image.url, 1600, 1000) : "")}
              alt={collection?.title ?? handle}
              className="absolute inset-0 h-full w-full object-cover"
              showToggle
            />
          ) : HERO_IMAGE_OVERRIDES[handle] ? (
            <img
              src={HERO_IMAGE_OVERRIDES[handle]}
              alt={collection?.title ?? handle}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : collection?.videoUrl ? (
            <video
              src={collection.videoUrl}
              poster={collection.image ? shopifyImg(collection.image.url, 1600, 1000) : undefined}
              controls
              loop
              playsInline
              preload="none"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : collection?.image ? (
            <img
              src={shopifyImg(collection.image.url, 1600, 1000)}
              srcSet={shopifySrcSet(collection.image.url, [800, 1200, 1600, 2000])}
              sizes="100vw"
              alt={collection.image.altText || collection.title}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
        </div>
        
        <div className="relative mx-auto flex h-full w-full max-w-6xl flex-col justify-end px-4 pb-12 sm:px-6 md:pb-16">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-2 t-eyebrow-lg text-[oklch(0.96_0.01_80)]/90 transition-colors hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" /> {t("nav.home")}
          </Link>
          {adminHeroText.hidden ? null : (
            <>
              <p className={"t-micro uppercase tracking-[var(--tracking-wider)] " + (HERO_MOBILE_IMAGE_OVERRIDES[handle] ? "text-[oklch(0.96_0.01_80)]/85 md:text-primary/90" : "text-primary/90")}>
                {t("section.collections")}
              </p>
              <h1 className={"mt-3 t-h1 text-[oklch(0.96_0.01_80)] md:text-6xl" + (HERO_MOBILE_IMAGE_OVERRIDES[handle] ? " hidden md:block" : "")}>
                {adminHeroText.title
                  ? adminHeroText.title
                  : HERO_TITLE_I18N_OVERRIDE[handle]
                    ? t(HERO_TITLE_I18N_OVERRIDE[handle] as Parameters<typeof t>[0])
                    : (collection?.title ?? handle)}
              </h1>
              {(adminHeroText.subtitle || collection?.description) && (
                <p className="mt-4 line-clamp-2 max-w-2xl t-body-lg text-[oklch(0.92_0.01_80)]/90">
                  {adminHeroText.subtitle || collection?.description}
                </p>
              )}
            </>
          )}

        </div>
      </section>


      <CollectionTabsNav activeHandle={handle} />

      <div className="mx-auto w-full max-w-6xl px-4 pt-3 pb-1 sm:px-6">
        <SiteBreadcrumbs scope="public" />
      </div>

      {/* Collection meta bar — Nemro/Dawn style */}
      <section className="bg-background">
        <div className="mx-auto flex w-full max-w-[1440px] items-center justify-between px-4 py-6 sm:px-6 md:py-8">
          <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
            {products.length}{" "}
            {locale === "ar" ? "قطعة" : products.length === 1 ? "item" : "items"}
          </p>
        </div>
      </section>


      {/* Products — يطابق نمط الهوم: كاروسيل موبايل ٨٥vw + شبكة ديسكتوب بخطوط رفيعة */}
      <section className="w-full border-y border-foreground bg-background">
        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className={
                  "aspect-[3/4] animate-pulse bg-secondary" +
                  (i < 3 ? " border-e border-foreground" : "")
                }
              />
            ))}
          </div>
        ) : products.length > 0 ? (
          (() => {
            // العرض ثابت ٤ أعمدة على الديسكتوب دائماً — لو المنتجات أقلّ
            // نملأ الفراغ ببلوكات فارغة بدل تكبير البطاقات.
            const desktopCols = 4;
            const fillers = Math.max(0, desktopCols - products.length);
            return (
              <>
                {/* موبايل: عرض مجلّة (منتج واحد لكلّ صفّ بالتناوب) */}
                <div className="md:hidden">
                  <ShopMobileEditorial products={products} eager />
                </div>
                <div className="hidden md:grid md:grid-cols-4 md:overflow-visible md:px-6 md:pb-0">
                  {products.map((p: typeof products[number], i: number) => {
                    const rowsDesktop = Math.ceil((products.length + fillers) / desktopCols);
                    const isLastRowDesktop = Math.floor(i / desktopCols) === rowsDesktop - 1;
                    const isLastColDesktop = i % desktopCols === desktopCols - 1;
                    return (
                      <div
                        key={p.node.id}
                        className={
                          "flex flex-col self-stretch " +
                          (isLastColDesktop ? "" : "md:border-e md:border-foreground ") +
                          "md:border-b md:border-foreground"
                        }

                      >
                        <ProductCard product={p} priority={i < 2} lcp={i === 0} />
                      </div>
                    );
                  })}
                  {Array.from({ length: fillers }).map((_, i) => (
                    <div
                      key={`filler-${i}`}
                      aria-hidden
                      className={
                        "hidden md:block md:self-stretch " +
                        (i < fillers - 1 ? "md:border-e md:border-foreground" : "")
                      }
                    />

                  ))}
                </div>
              </>
            );
          })()

        ) : (
          <div className="mx-4 my-16 border border-dashed border-border py-16 text-center sm:mx-6">
            <p className="t-h2">{t("shop.empty")}</p>
            <p className="mt-2 t-body text-muted-foreground">{t("shop.empty_sub")}</p>
          </div>
        )}
        <ShopAllFragrancesBar collection={{ handle }} />
      </section>

      {/* حفاظاً على استخدام pickLocalized للـSSR/head — noop */}
      <span className="sr-only">{pickLocalized("", "", locale, "")}</span>
    </SiteShell>
  );
}
